<template>
  <v-container>
    <v-row>
      <v-col>
        <h3 class="text-center">{{ pageTitle }}</h3>
      </v-col>
    </v-row>
    <ImportRunStatusBanner
      :chunkID="chunkID"
      :destination="destination"
      @change="onRunStatus"
    />
    <v-row>
      <v-col cols="4" align="center">
        <p class="font-weight-bold">Imported</p>
        <p>(Import {{ importedDataCaption }})</p>
        <v-btn :disabled="true">
          DONE
        </v-btn>
      </v-col>
      <v-col cols="4" align="center">
        <p class="font-weight-bold">Mapping</p>
        <p>(Match imported {{ importedDataCaption }})</p>
        <v-btn :disabled="true">
          DONE
        </v-btn>
      </v-col>
      <v-col cols="4" align="center">
        <p class="font-weight-bold">Completed(Process)</p>
        <p>(Mark matched {{ importedDataCaption }} paid?)</p>
        <v-btn
          :disabled="isUpdated || !resultsReady || noRowsFound"
          depressed
          color="primary"
          @click="updateTransactions"
        >
          {{ isUpdated ? 'Done' : 'Proceed' }}
        </v-btn>
      </v-col>
    </v-row>
    <v-alert
      v-if="noRowsFound"
      id="tmc-import-no-rows"
      type="info"
      dense
      text
      class="mt-4"
    >
      No paid {{ importedDataCaption }} found in this file (rows without a check number and debit
      amount, such as deposits, are skipped).
    </v-alert>
    <template v-if="resultsReady && !noRowsFound">
    <p></p>
    <hr>
    <p></p>
    <h3 class="text-center">MATCHED {{ importedDataCaption.toUpperCase() }}</h3>
    <template>
      <v-data-table
        :headers="transactionDetails.matchedData.headers"
        :items="transactionDetails.matchedData.data"
        :hide-default-footer="true"
        disable-pagination
        class="elevation-1"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td v-for="header in transactionDetails.matchedData.headers" :key="header.value">
              <span v-if="header.formatter === 'shortDate'">{{ toShortDate(item[header.value] || null) }}</span>
              <span v-else>{{ item[header.value] }}</span>
            </td>
          </tr>
        </template>
        <template slot="body.append">
          <tr class="pink--text">
              <td colspan="3">
                Total for matched {{ importedDataCaption }}:
                ${{amountFormat(transactionDetails.matchedData.totalAmount) }}
              </td>
          </tr>
      </template>
      </v-data-table>
    </template>
    <p></p>
    <hr>
    <p></p>
    <h3 class="text-center">UNMATCHED {{ importedDataCaption.toUpperCase() }}</h3>
    <template>
      <v-data-table
        :headers="transactionDetails.unmatchedData.headers"
        :items="transactionDetails.unmatchedData.data"
        :hide-default-footer="true"
        disable-pagination
        class="elevation-1"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td v-for="header in transactionDetails.unmatchedData.headers" :key="header.value">
              <span v-if="header.formatter === 'shortDate'">{{ toShortDate(item[header.value] || null) }}</span>
              <span v-else>{{ item[header.value] }}</span>
            </td>
          </tr>
        </template>
        <template slot="body.append">
          <tr class="pink--text">
              <td colspan="3">
                Total for unmatched {{ importedDataCaption }}:
                ${{ amountFormat(transactionDetails.unmatchedData.totalAmount) }}
              </td>
          </tr>
        </template>
      </v-data-table>
    </template>
    </template>
    <v-overlay :value="overlay">
      <v-progress-circular
        :rotate="-90"
        :size="100"
        :width="15"
        :value="value"
        color="primary"
      >
        {{ value }}
      </v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import {
  amountFormat,
  amountToNumber,
} from '../../../../util/shared/vue-global';
import { toShortDate } from '../../../../util/shared/tmc-global';
import { programToModel } from '../../../War/Transaction/warTransaction.util';
import ImportRunStatusBanner from '../../../bulk/ImportRunStatusBanner.vue';

// the rows of a matched/unmatched section of the processed data, [] while the section is missing
const rowsOf = (section) => (section || {}).data || [];

const initdata = () => ({
  overlay: false,
  value: 0,
  isUpdated: false,
  // undefined = not known yet, null = no marker for this run (imported before markers existed),
  // otherwise the marker the server keeps for the run
  runStatus: undefined,
  resultsLoaded: false,
  // the staged rows were read back successfully (resultsLoaded only says the read was started)
  resultsFetched: false,
});

export default {
  name: 'ImportFileProcessedDetails',
  components: { ImportRunStatusBanner },
  data: () => initdata(),
  props: {
    pageTitle: {
      type: String,
      default: '',
    },
    // the import run the screen is about (the report URL's ?chunkID=...); without one the server
    // falls back to the latest run of `destination`
    chunkID: {
      type: String,
      default: '',
    },
    // the import destination the run marker is filed under
    destination: {
      type: String,
      default: 'importPaidWarrants',
    },
    importedDataCaption: {
      type: String,
      default: '',
    },
    markPaidType: {
      type: String,
      default: '',
    },
    program: {
      type: String,
      default: '',
    },
    gqlfields: {
      type: Object,
      default: () => {},
    },
    dataMapper: {
      type: Function,
      default: () => {},
    },
    loadImportedData: {
      type: Function,
      default: () => {},
    },
    dataHeaders: {
      type: Function,
      default: () => {},
    },
    upsertDataMapper: {
      type: Function,
      default: () => {},
    },
    upsertImportedData: {
      type: Function,
      default: () => {},
    },
    getTransactions: {
      type: Function,
      default: () => {},
    },
    upsertGroupedData: {
      type: Function,
      default: () => {},
    },
  },
  created() {
  },
  computed: {
    ...mapState({
      transactionDetails: (state) => state.Bulk.processedFileData,
    }),
    // The staged rows are whatever the import has written so far, so nothing is listed - and nothing
    // can be marked paid - until the server says the import finished.
    resultsReady() {
      return this.runStatus === null
        || (!!this.runStatus && this.runStatus.status === 'complete');
    },
    // with no chunkID in the URL the server picked the latest run, so use the one it picked
    runChunkID() {
      return this.chunkID || (this.runStatus && this.runStatus.chunkID) || '';
    },
    // The import finished and read back fine, but staged nothing: a bank file of only deposits has
    // no paid warrants in it. Not a failure, so say so instead of showing two empty lists.
    noRowsFound() {
      const details = this.transactionDetails || {};
      return this.resultsReady && this.resultsFetched
        && !rowsOf(details.matchedData).length
        && !rowsOf(details.unmatchedData).length;
    },
  },
  methods: {
    ...mapMutations([
      'setPrintScreen',
    ]),
    amountFormat,
    toShortDate,
    ...mapActions(['flashInfo', 'flashError']),
    ...mapActions('war/transaction', [
      'warBulkPaidTrans',
    ]),
    onRunStatus(run) {
      this.runStatus = run;
      if (this.resultsReady && !this.resultsLoaded) {
        this.resultsLoaded = true;
        // returned so Vue's event-handler error handling sees a rejection instead of it going unhandled
        return this.loadResults();
      }
      return undefined;
    },
    async loadResults() {
      const loaded = await this.loadImportedData({
        gqlfields: this.gqlfields,
        chunkID: this.runChunkID,
        postProccess: ({ transData }) => {
          const processedFileData = {
            matchedData: {
              headers: this.dataHeaders().matched,
              data: [],
              totalAmount: 0,
            },
            unmatchedData: {
              headers: this.dataHeaders().unmatched,
              data: [],
              totalAmount: 0,
            },
          };
          if (Array.isArray(transData) && transData.length > 0) {
            for (let i = 0; i < transData.length; i += 1) {
              const trans = transData[i];
              if (trans.transactionDetails !== null) {
                processedFileData.matchedData.data.push(this.dataMapper(trans, true));
                processedFileData.matchedData.totalAmount += amountToNumber(trans.amount);
              } else {
                processedFileData.unmatchedData.data.push(this.dataMapper(trans));
                processedFileData.unmatchedData.totalAmount += amountToNumber(trans.amount);
              }
            }
          }
          return processedFileData;
        },
      });
      // the load action answers with what it committed, and with nothing when it could not load
      this.resultsFetched = !!loaded;
    },
    async updateMatchedTransactionStatus() {
      if (window.confirm('Are you sure want to update all matched records?')) {
        const matchedData = ((this.transactionDetails || {}).matchedData || {}).data || [];
        this.overlay = true;
        for (let i = 0; i < matchedData.length; i += 1) {
          const item = await this.upsertDataMapper({ ...matchedData[i] });
          await this.upsertImportedData(item);
          this.value = parseInt(((100 * (i + 1)) / matchedData.length), 10);
        }
        this.overlay = false;
        this.isUpdated = true;
        this.flashInfo(`Successfully ${matchedData.length} records have updated.`);
        this.$nextTick(() => {
          this.setPrintScreen(true);
          this.$nextTick(() => {
            window.print();
            this.$nextTick(() => {
              this.setPrintScreen(false);
            });
          });
        });
      }
    },
    async bulkBatchPayTrans(matchedData) {
      if (matchedData.length === 0) return;

      if (window.confirm('Are you sure want to update all matched records?')) {
        this.overlay = true;
        // extract ids with paid cancelled date
        const idsWithPaidDatesArr = [];
        for (let index = 0; index < matchedData.length; index += 1) {
          const idsWithPaidDateObj = {};
          const element = matchedData[index];
          idsWithPaidDateObj._id = element.transactionId;
          idsWithPaidDateObj.paidCancelledDate = element.date;
          idsWithPaidDatesArr.push(idsWithPaidDateObj);
        }
        const item = {
          _idsAndPaidDate: idsWithPaidDatesArr,
          program: programToModel(this.program),
        };
        // Report what the server said it wrote, not what we sent it. When mark-paid silently
        // matched nothing (the mongoose 8 fund-id regression) this screen still announced
        // "Successfully 14 records have updated." over 14 warrants that were all still
        // outstanding, which is why the incident ran for as long as it did.
        const result = (await this.warBulkPaidTrans({ item })) || {};
        const paidCount = (result.successIds || []).length;
        const total = typeof result.totalIds === 'number' ? result.totalIds : matchedData.length;
        this.overlay = false;
        if (paidCount === 0) {
          // No totalIds at all means the mutation itself failed and the store action has
          // already flashed its own error - do not stack a second one on top.
          if (typeof result.totalIds === 'number') {
            this.flashError(`No records were marked paid. ${total} records were left unchanged.`);
          }
          return;
        }
        this.isUpdated = true;
        this.flashInfo(`Successfully marked ${paidCount} of ${total} records paid.`);
        this.$nextTick(() => {
          this.setPrintScreen(true);
          this.$nextTick(() => {
            window.print();
            this.$nextTick(() => {
              this.setPrintScreen(false);
            });
          });
        });
      }
    },
    async bulkGroupPayTrans(matchedData) {
      if (window.confirm('Are you sure want to update all matched records?')) {
        this.overlay = true;
        const fetchedGroupTransactions = await this.getTransactions(matchedData);
        let index = 0;
        const groupedTransLength = Object.keys(fetchedGroupTransactions).length;
        for (const [key, value] of Object.entries(fetchedGroupTransactions)) {
          await this.upsertGroupedData(key, value);
          this.value = parseInt(((100 * (index + 1)) / groupedTransLength), 10);
          index += 1;
        }
        this.overlay = false;
        this.isUpdated = true;
        this.flashInfo(`Successfully ${matchedData.length} records have updated.`);
        this.setPrintScreen(true);
        this.$nextTick(() => {
          this.setPrintScreen(true);
          this.$nextTick(() => {
            window.print();
            this.$nextTick(() => {
              this.setPrintScreen(false);
            });
          });
        });
      }
    },
    async updateTransactions() {
      if (!this.resultsReady) {
        return;
      }
      const matchedData = ((this.transactionDetails || {}).matchedData || {}).data || [];
      if (this.markPaidType === 'group') {
        await this.bulkGroupPayTrans(matchedData);
      } else if (this.markPaidType === 'batch') {
        await this.bulkBatchPayTrans(matchedData);
      } else {
        this.updateMatchedTransactionStatus();
      }
    },
  },
};
</script>
